import React, { useState, useEffect, useMemo, useRef } from 'react';
import IphoneProductCard from './IphoneProductCard';
import { useSelector } from 'react-redux';
import Nav from '../../Nav/Nav';
import ScrollTop from '../../Scrolltop';
import { IoIosArrowDown, IoIosSearch } from 'react-icons/io';

import { useLocation, useParams } from 'react-router-dom';
import RedirectBack from '../../../resuable/RedirectBack';

const ProductGrid = () => {
  const iphoneData = useSelector((state) => state.products.iphoneData);
  const airpodsData = useSelector(
    (state) => state.products.airpodsProductDetails
  );

  const mac = useSelector((state) => state.products.macProducts);
  const ipad = useSelector((state) => state.products.iPadProducts);
  const applewatch = useSelector((state) => state.products.appleWatchProducts);

  // samsung
  const galaxySSeries = useSelector((state) => state.products.galaxySSeries);
  const galaxyASeries = useSelector((state) => state.products.galaxyASeries);
  const galaxyFSeries = useSelector((state) => state.products.galaxyFSeries);
  const galaxyZSeries = useSelector((state) => state.products.galaxyZSeries);
  const galaxyTab = useSelector((state) => state.products.tab);

  // oneplus
  const onePlusData = useSelector((state) => state.products.onePlusData);

  const { type } = useParams();
  const location = useLocation();

  const [typeData, setTypeData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [priceSort, setPriceSort] = useState('none');
  const [isPriceDropdownOpen, setIsPriceDropdownOpen] = useState(false);
  const priceDropdownRef = useRef(null);

  const parsePrice = (value) =>
    Number(String(value ?? '').replace(/[^\d]/g, '')) || 0;

  useEffect(() => {
    if (type === 'iphone') {
      setTypeData(iphoneData);
    } else if (type === 'airpods') {
      setTypeData(airpodsData);
    } else if (type === 'mac') {
      setTypeData(mac);
    } else if (type === 'ipad') {
      setTypeData(ipad);
    } else if (type === 'applewatch') {
      setTypeData(applewatch);
    } else if (type === 'galaxySSeries') {
      setTypeData(galaxySSeries);
    } else if (type === 'galaxyASeries') {
      setTypeData(galaxyASeries);
    } else if (type === 'galaxyFSeries') {
      setTypeData(galaxyFSeries);
    } else if (type === 'galaxyZSeries') {
      setTypeData(galaxyZSeries);
    } else if (type === 'galaxyTab') {   
      setTypeData(galaxyTab);
    } else if (type === 'oneplus') {
      const searchParams = new URLSearchParams(location.search);
      const selectedSeries = searchParams.get('series');

      if (selectedSeries === 'nord') {
        setTypeData(
          onePlusData.filter((product) =>
            product.title.toLowerCase().includes('nord')
          )
        );
      } else if (selectedSeries === '15s') {
        setTypeData(
          onePlusData.filter((product) =>
            product.title.toLowerCase().includes('15s')
          )
        );
      } else if (selectedSeries === '15') {
        setTypeData(
          onePlusData.filter((product) =>
            product.title.toLowerCase().startsWith('oneplus 15 (')
          )
        );
      } else if (selectedSeries === 'flagship') {
        setTypeData(
          onePlusData.filter((product) => {
            const title = product.title.toLowerCase();
            return !title.includes('nord') && !title.includes('pad');
          })
        );
      } else if (selectedSeries === 'pad') {
        setTypeData(
          onePlusData.filter((product) =>
            product.title.toLowerCase().includes('oneplus pad')
          )
        );
      } else if (selectedSeries === 'padgo2') {
        setTypeData(
          onePlusData.filter((product) =>
            product.title.toLowerCase().includes('pad go 2')
          )
        );
      } else {
        setTypeData(onePlusData);
      }
    }
  }, [
    type,
    location.search,
    iphoneData,
    airpodsData,
    applewatch,
    ipad,
    mac,
    galaxySSeries,
    galaxyASeries,
    galaxyFSeries,
    galaxyZSeries,
    galaxyTab,
    onePlusData,
  ]); // Dependencies

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        priceDropdownRef.current &&
        !priceDropdownRef.current.contains(event.target)
      ) {
        setIsPriceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    const filtered = typeData.filter((item) => {
      const priceValue = parsePrice(item.discountedPrice);
      const searchableText = [
        item.title,
        item.discountedPrice,
        ...(Array.isArray(item.colors) ? item.colors : []),
      ]
        .join(' ')
        .toLowerCase();

      const matchesText =
        normalizedSearch === '' || searchableText.includes(normalizedSearch);

      return matchesText && priceValue > 0;
    });

    if (priceSort === 'lowToHigh') {
      return [...filtered].sort(
        (a, b) => parsePrice(a.discountedPrice) - parsePrice(b.discountedPrice)
      );
    }

    if (priceSort === 'highToLow') {
      return [...filtered].sort(
        (a, b) => parsePrice(b.discountedPrice) - parsePrice(a.discountedPrice)
      );
    }

    return filtered;
  }, [typeData, searchText, priceSort]);

  const priceSortLabel = {
    none: 'Sort by Price',
    lowToHigh: 'Low to High',
    highToLow: 'High to Low',
  };

  return (
    <>
      <ScrollTop />
      <Nav />
      <div className="product-filters">
        <RedirectBack steps={-1} />
        <div className="filters-right">
          <div className="search-field-wrap">
            <input
              type="text"
              className="search-field"
              placeholder="Search products..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => setSearchText('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
            <span className="search-icon" aria-hidden="true">
              <IoIosSearch />
            </span>
          </div>

          <div className="price-filters">
            <div className="price-sort-custom" ref={priceDropdownRef}>
              <button
                type="button"
                className={`price-sort-select ${isPriceDropdownOpen ? 'is-open' : ''}`}
                aria-haspopup="listbox"
                aria-expanded={isPriceDropdownOpen}
                onClick={() => setIsPriceDropdownOpen((prev) => !prev)}
              >
                <span>{priceSortLabel[priceSort]}</span>
                <IoIosArrowDown className="price-sort-arrow" />
              </button>

              {isPriceDropdownOpen && (
                <div className="price-sort-menu" role="listbox">
                  <button
                    type="button"
                    className={`price-sort-option ${priceSort === 'none' ? 'active' : ''}`}
                    onClick={() => {
                      setPriceSort('none');
                      setIsPriceDropdownOpen(false);
                    }}
                  >
                    Sort by Price
                  </button>
                  <button
                    type="button"
                    className={`price-sort-option ${priceSort === 'lowToHigh' ? 'active' : ''}`}
                    onClick={() => {
                      setPriceSort('lowToHigh');
                      setIsPriceDropdownOpen(false);
                    }}
                  >
                    Low to High
                  </button>
                  <button
                    type="button"
                    className={`price-sort-option ${priceSort === 'highToLow' ? 'active' : ''}`}
                    onClick={() => {
                      setPriceSort('highToLow');
                      setIsPriceDropdownOpen(false);
                    }}
                  >
                    High to Low
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* PRODUCT LIST */}
      <div className="iphone-container">
        {filteredProducts.map((item) => (
          <IphoneProductCard
            key={`${type}-${item.id}-${item.title}`}
            id={item.id}
            image={item.productImage}
            name={item.title}
            // storage={item.iphoneStorage}
            price={item.discountedPrice}
            color={item.colors}
            type={type}
          />
        ))}
      </div>
    </>
  );
};

export default ProductGrid;
